import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaShoppingCart, FaUserPlus, FaTimes, FaSave, FaDownload, FaEnvelope, FaUndo, FaEdit, FaPlus } from "react-icons/fa";
import { useDropzone } from 'react-dropzone';
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { handleApiResponse } from './../helpers/FormValidation';
import { showSuccessMessage, showErrorMessage } from "./../utils/alertHelper";
import moment from 'moment';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "bootstrap/dist/css/bootstrap.min.css";
import { Container,Tab, Tabs} from "react-bootstrap";
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import TopNavbar from "../components/TopNavbar";


const UpdatePosInvoice = () => {


  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
      // Theme Management
      useEffect(() => {
        document.body.className = theme;
        localStorage.setItem("theme", theme);
      }, [theme]);
    
      const toggleTheme = () => {
        setTheme(theme === "light" ? "dark" : "light");
      };
      const [user, setUser] = useState(null);


  // const { id: invoiceId } = useParams();
  const { invoice_id: invoiceId } = useParams();

  const navigate = useNavigate();
  const invoiceRef = useRef();
  const baseURL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");
  
  // Get user info from localStorage
  const login_user = JSON.parse(localStorage.getItem("login_user") || "{}");
  const { location_id, device_id, user_id } = login_user || {};

  // State management
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState({
    customer_id: "",
    invoice_date: moment().format("YYYY-MM-DD"),
    due_date: moment().add(7, 'days').format("YYYY-MM-DD"),
    notes: "",
    po_no: "",
    to_address: "",
    close_date: "",
    carriage: "",
    discount: "",
    invoice_image: "",
    status: ""
  });

  const [products, setProducts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [taxConfig, setTaxConfig] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);

  // Modal states
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  // Form states
  const [item, setItem] = useState({
    product_id: '',
    product_name: '',
    quantity: 1,
    price: 0,
    discount: 0,
    tax: 'Exempt',
    description: '',
    searchItem: ''
  });

  const [paymentData, setPaymentData] = useState({
    amount: "",
    method: "Cash",
    date: moment().format("YYYY-MM-DD")
  });

  const [refund, setRefund] = useState({
    reason: "",
    refund_amount: 0,
    refundPaymentMethod: "deposit",
    user_id: user_id,
    items: [],
  });

  const [emailInvoice, setEmailInvoice] = useState({
    template: "template_1",
    to: '',
    cc: "",
    bcc: "",
    subject: `Invoice #${invoiceId}`,
    messageBody: "Please find attached invoice for your reference.",
  });

  // Dropzone for image upload
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: useCallback((acceptedFiles) => {
      if (acceptedFiles?.length) {
        const file = acceptedFiles[0];
        setSelectedImageFile(file);
        setSelectedImage(URL.createObjectURL(file));
      }
    }, []),
    accept: 'image/*',
  });

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchInvoiceDetails(),
          fetchCustomers(),
          fetchTaxConfig()
        ]);
      } catch (error) {
        showErrorMessage("Failed to load initial data");
        console.error("Initial data error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [invoiceId]);

  // API calls
  const fetchInvoiceDetails = async () => {
    try {
      const response = await axios.get(`${baseURL}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      const data = response.data.data;
      setInvoiceData({
        ...data,
        customer_id: data.customer?.id || '',
        invoice_date: moment(data.dates?.invoice_date).format("YYYY-MM-DD"),
        due_date: moment(data.dates?.due_date).format("YYYY-MM-DD"),
        close_date: data.dates?.close_date ? moment(data.dates.close_date).format("YYYY-MM-DD") : "",
        notes: data.additional_info?.notes || "",
        po_no: data.additional_info?.po_no || "",
        to_address: data.additional_info?.to_address || "",
        carriage: data.financials?.carriage || 0,
        discount: data.financials?.discount || 0,
        invoice_image: data.additional_info?.invoice_image || "",
        status: data.status_info?.status || ""
      });
      
      setProducts(data.items || []);
      setPayments(data.payments || []);
  
      // Set email recipient if customer has email
      if (data.customer?.email) {
        setEmailInvoice(prev => ({ ...prev, to: data.customer.email }));
      }
    } catch (error) {
      console.error("Error fetching invoice:", error);
      showErrorMessage("Failed to load invoice details");
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${baseURL}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Handle 204 No Content
      if (response.status === 204) {
        setCustomers([]); // Set empty array if no content
        return;
      }
      
      // Normal response handling
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
      showErrorMessage("Failed to fetch customer details.");
      setCustomers([]); // Fallback empty array
    }
  };

  const fetchTaxConfig = async () => {
    try {
      const response = await axios.get(`${baseURL}/taxconfig`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTaxConfig(response.data.data || []);
    } catch (error) {
      console.error("Error fetching tax config:", error);
    }
  };

  const handleSearch = async (query) => {
    if (!query) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axios.get(`${baseURL}/invoices/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { query },
      });
      setSearchResults(response.data.data || []);
    } catch (error) {
      console.error("Search error:", error);
      showErrorMessage("Failed to search products");
    }
  };

  // Form handlers
  const handleInvoiceChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setItem(prev => ({ ...prev, [name]: value }));
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({ ...prev, [name]: value }));
  };

  const handleRefundChange = (e) => {
    const { name, value } = e.target;
    setRefund(prev => ({ ...prev, [name]: value }));
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailInvoice(prev => ({ ...prev, [name]: value }));
  };

  // CRUD operations
  const handleUpdateInvoice = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    // Map the fields to match the API structure
    formData.append('customer_id', invoiceData.customer_id);
    formData.append('invoice_date', invoiceData.invoice_date);
    formData.append('due_date', invoiceData.due_date);
    formData.append('notes', invoiceData.notes);
    formData.append('po_no', invoiceData.po_no);
    formData.append('to_address', invoiceData.to_address);
    formData.append('carriage', invoiceData.carriage);
    formData.append('discount', invoiceData.discount);
    
    if (selectedImageFile) {
      formData.append("invoice_image", selectedImageFile);
    }
  
    formData.append('_method', 'PUT');
  
    try {
      const response = await axios.post(`${baseURL}/invoices/${invoiceId}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
  
      if (response.status === 200) {
        showSuccessMessage("Invoice updated successfully!");
        fetchInvoiceDetails();
      }
    } catch (error) {
      console.error("Update error:", error);
      handleApiResponse(error.response?.data, "Failed to update invoice", null, navigate);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${baseURL}/invoices/${invoiceId}/add-item`, 
        item,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        showSuccessMessage("Item added successfully!");
        fetchInvoiceDetails();
        setShowAddItemModal(false);
        resetItemForm();
      }
    } catch (error) {
      console.error("Add item error:", error);
      handleApiResponse(error.response?.data, "Failed to add item", null, navigate);
    }
  };

  // const handleEditItem = (product) => {
  //   setItem({
  //     product_id: product.id,
  //     product_name: product.name,
  //     quantity: product.qty,
  //     price: product.price,
  //     discount: product.discountPrice,
  //     tax: product.tax || 'Exempt',
  //     description: product.description || ''
  //   });
  //   setShowEditItemModal(true);
  // };

  // const handleUpdateItem = async () => {
  //   try {
  //     const response = await axios.put(
  //       `${baseURL}/invoices/${invoiceId}/update-item/${item.product_id}`, 
  //       item,
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );

  //     if (response.status === 200) {
  //       showSuccessMessage("Item updated successfully!");
  //       fetchInvoiceDetails();
  //       setShowEditItemModal(false);
  //     }
  //   } catch (error) {
  //     console.error("Update item error:", error);
  //     handleApiResponse(error.response?.data, "Failed to update item", null, navigate);
  //   }
  // };

  const handleEditItem = (product) => {
    setItem({
      product_id: product.id,
      product_name: product.name,
      quantity: product.qty || 1,  // Default to 1 if empty
      price: product.price || 0,    // Default to 0 if empty
      discount: product.discountPrice || 0,
      tax: product.tax || 'Exempt',
      description: product.description || '',
      batch_number: product.batch_number || '',
      expiry_date: product.expiry_date || ''
    });
    setShowEditItemModal(true);
  };
  
  const handleUpdateItem = async () => {
    // Validate required fields
    if (!item.quantity || item.quantity < 1) {
      showErrorMessage("Quantity must be at least 1");
      return;
    }
  
    if (!item.price || item.price < 0) {
      showErrorMessage("Price must be a positive number");
      return;
    }
  
    // Validate discount doesn't exceed item total
    const maxDiscount = item.price * item.quantity;
    if (item.discount > maxDiscount) {
      showErrorMessage(`Discount cannot exceed ${maxDiscount.toFixed(2)}`);
      return;
    }
  
    try {
      const response = await axios.put(
        `${baseURL}/invoices/${invoiceId}/update-item/${item.product_id}`, 
        {
          quantity: Number(item.quantity),
          price: Number(item.price),
          discount: Number(item.discount),
          tax: item.tax,
          description: item.description,
          batch_number: item.batch_number,
          expiry_date: item.expiry_date
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
  
      if (response.status === 200) {
        showSuccessMessage("Item updated successfully!");
        fetchInvoiceDetails();
        setShowEditItemModal(false);
      }
    } catch (error) {
      console.error("Update item error:", error);
      const errorMessage = error.response?.data?.message || "Failed to update item";
      showErrorMessage(errorMessage);
    }
  };

  const handleRemoveItem = async (productId) => {
    if (!window.confirm("Are you sure you want to remove this item?")) return;
    
    try {
      const response = await axios.delete(
        `${baseURL}/invoices/${invoiceId}/remove-item/${productId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        showSuccessMessage("Item removed successfully!");
        fetchInvoiceDetails();
      }
    } catch (error) {
      console.error("Remove item error:", error);
      showErrorMessage("Failed to remove item");
    }
  };

  const handleAddPayment = async () => {
    try {
      const response = await axios.post(
        `${baseURL}/invoices/${invoiceId}/add-payment`,
        paymentData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        showSuccessMessage("Payment added successfully!");
        fetchInvoiceDetails();
        setShowAddPaymentModal(false);
        setPaymentData({
          amount: "",
          method: "Cash",
          date: moment().format("YYYY-MM-DD")
        });
      }
    } catch (error) { 
      showSuccessMessage(error.response.data.message);
      console.error("Add payment error:", error);
      // handleApiResponse(error.response?.data.message, "Failed to add payment", null, navigate);
    }
  };

  const handleRemovePayment = async (paymentId) => {
    if (!window.confirm("Are you sure you want to remove this payment?")) return;
    
    try {
      const response = await axios.delete(
        `${baseURL}/invoices/${invoiceId}/remove-payment/${paymentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        showSuccessMessage("Payment removed successfully!");
        fetchInvoiceDetails();
      }
    } catch (error) {
      console.error("Remove payment error:", error);
      showErrorMessage("Failed to remove payment");
    }
  };

  const handleRefundItemChange = (e, index, salePrice,discountValue) => {
    const quantity = Math.max(0, Math.min(products[index].quantity, parseInt(e.target.value || "0", 10)));
    const baseAmount = quantity * salePrice;
    let amount =0;
    if(discountValue > 0){
       amount = baseAmount - discountValue;
    }
    else{
       amount = baseAmount;
    }
  
    setRefund(prev => {
      const updatedItems = [...prev.items];
      updatedItems[index] = { 
        product_id: products[index].product_id,
        id: products[index].id, 
        quantity, 
        amount 
      };
  
      const refund_amount = updatedItems.reduce((sum, item) => sum + (item?.amount || 0), 0);
      
      return { ...prev, items: updatedItems, refund_amount };
    });
  };

  const handleProcessRefund = async () => {
    try {
      const response = await axios.post(
        `${baseURL}/invoices/${invoiceId}/process-refund`,
        { invoice_id: invoiceId, ...refund },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        showSuccessMessage("Refund processed successfully!");
        setShowRefundModal(false);
        fetchInvoiceDetails();
        setRefund({
          reason: "",
          refund_amount: 0,
          refundPaymentMethod: "deposit",
          user_id: user_id,
          items: [],
        });
      }
    } catch (error) {
      console.error("Refund error:", error);
      handleApiResponse(error.response?.data, "Failed to process refund", null, navigate);
    }
  };

  const handleEmailInvoice = async () => {
    try {
      const response = await axios.post(
        `${baseURL}/invoices/${invoiceId}/email`,
        emailInvoice,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        showSuccessMessage("Invoice emailed successfully!");
        setShowEmailModal(false);
      }
    } catch (error) {
      console.error("Email error:", error);
      showErrorMessage("Failed to send email");
    }
  };

  // Helper functions
  const resetItemForm = () => {
    setItem({
      product_id: '',
      product_name: '',
      quantity: 1,
      price: 0,
      discount: 0,
      tax: 'Exempt',
      description: '',
      searchItem: ''
    });
    setSearchResults([]);
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${baseURL}/invoices/${invoiceId}/generate-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("PDF error:", error);
      showErrorMessage("Failed to download PDF");
    }
  };

  const handleGeneratePDF = async () => {
    const element = invoiceRef.current;
    const canvas = await html2canvas(element, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`invoice_${invoiceId}.pdf`);
  };

  // Calculate totals
  const subtotal = invoiceData.subtotal || 0;
  const total = invoiceData.total || 0;
  const tax = total - subtotal;
  const discountAmount = subtotal - total;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard ${theme}`}>
<Container fluid className={`vh-100 ${theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}`}>
    <TopNavbar user={user} toggleTheme={toggleTheme} theme={theme} />

    <div className="page-wrapper" ref={invoiceRef}>
      <div className="page-content-wrapper">
        <div className="page-content">
          <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
            <div className="breadcrumb-title pe-3">POS Invoices</div>
            <div className="ps-3">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-0 p-0">
                  <li className="breadcrumb-item">
                    <i className="bx bx-basket"></i>
                  </li>
                  <li className="breadcrumb-item active">Edit POS Invoice</li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="row">
            <div className="col-xl-12 mx-auto">
              <div  className={`card ${theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}`}>
                <div className="card-body">
                  <form onSubmit={handleUpdateInvoice}>
                    <p className='text-primary'>Transaction Details:</p>
                    
                    {/* Invoice Header */}
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <div className="d-flex justify-content-between">
                          <span>Status:</span>
                          <span className={`badge bg-${invoiceData.invoice_status === 'paid' ? 'success' : 'warning'}`}>
                            {invoiceData.invoice_status}
                          </span>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="d-flex justify-content-between">
                          <span>Reference:</span>
                          <span>{invoiceData.ref}</span>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="d-flex justify-content-between">
                          <span>Date:</span>
                          <span>{moment(invoiceData.invoice_date).format('DD/MM/YYYY')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Customer and Dates */}
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <label className="form-label">Customer</label>
                      
                        <select
  className="form-select"
  name="customer_id"
  value={invoiceData.customer_id}
  onChange={handleInvoiceChange}
  required
>
  <option value="">Select Customer</option>
  {customers.map(customer => (
    <option 
      key={customer.id} 
      value={customer.id}
    >
      {customer.name}
    </option>
  ))}
</select>

                        
                      </div>

                      <div className="col-md-4">
                        <label className="form-label">Invoice Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="invoice_date"
                          value={invoiceData.invoice_date}
                          onChange={handleInvoiceChange}
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="form-label">Due Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="due_date"
                          value={invoiceData.due_date}
                          onChange={handleInvoiceChange}
                        />
                      </div>
                    </div>

                    {/* Notes and Additional Info */}
                    <div className="row mb-3">
                      <div className="col-md-12">
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-control"
                          name="notes"
                          value={invoiceData.notes}
                          onChange={handleInvoiceChange}
                          rows="3"
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-4">
                        <label className="form-label">PO Number</label>
                        <input
                          type="text"
                          className="form-control"
                          name="po_no"
                          value={invoiceData.po_no}
                          onChange={handleInvoiceChange}
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="form-label">Carriage</label>
                        <input
                          type="text"
                          className="form-control"
                          name="carriage"
                          value={invoiceData.carriage}
                          onChange={handleInvoiceChange}
                        />
                      </div>

                      <div className="col-md-4">
                        <label className="form-label">Discount (%)</label>
                        <input
                          type="text"
                          className="form-control"
                          name="discount"
                          value={invoiceData.discount}
                          onChange={handleInvoiceChange}
                        />
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Invoice Image</label>
                        <div {...getRootProps({ className: 'dropzone border p-4 text-center' })}>
                          <input {...getInputProps()} />
                          {isDragActive ? (
                            <p>Drop the image here...</p>
                          ) : (
                            <p>Drag & drop an image here, or click to select</p>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        {selectedImage ? (
                          <img src={selectedImage} alt="Preview" className="img-fluid mt-2" style={{ maxHeight: '200px' }} />
                        ) : invoiceData.invoice_image ? (
                          <img 
                            src={`${baseURL.replace('/api', '')}${invoiceData.invoice_image}`} 
                            alt="Current Invoice" 
                            className="img-fluid mt-2" 
                            style={{ maxHeight: '200px' }} 
                          />
                        ) : null}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="row mt-3">
                      <div className="col-12 d-flex justify-content-center gap-3">
                        <button type="submit" className="btn btn-success px-4">
                        <FaSave className="me-1" /> Save Invoice
                        </button>
                        <button type="button" className="btn btn-secondary px-4" onClick={handleGeneratePDF}>
                        <FaDownload className="me-1" /> Download PDF
                        </button>
                      </div>
                    </div>
                  </form>

                  {/* Tabs */}
                  <div className="row mt-4">
                    <div className="col-12">
                      <ul className="nav nav-tabs" id="invoiceTabs" role="tablist">
                        <li className="nav-item" role="presentation">
                          <button className="nav-link active" id="details-tab" data-bs-toggle="tab" data-bs-target="#details" type="button" role="tab">
                            Details
                          </button>
                        </li>
                        <li className="nav-item" role="presentation">
                          <button className="nav-link" id="items-tab" data-bs-toggle="tab" data-bs-target="#items" type="button" role="tab">
                            Items ({products.length})
                          </button>
                        </li>
                        <li className="nav-item" role="presentation">
                          <button className="nav-link" id="payments-tab" data-bs-toggle="tab" data-bs-target="#payments" type="button" role="tab">
                            Payments ({payments.length})
                          </button>
                        </li>
                        <li className="nav-item" role="presentation">
                          <button className="nav-link" id="options-tab" data-bs-toggle="tab" data-bs-target="#options" type="button" role="tab">
                            Options
                          </button>
                        </li>
                      </ul>

                      <div className="tab-content p-3 border border-top-0" id="invoiceTabsContent">
                        {/* Details Tab */}
                        <div className="tab-pane fade show active" id="details" role="tabpanel">
                          <div className="row">
                            <div className="col-md-6">
                              <h5>Financial Summary</h5>
                              <table  className={`table table-sm ${
                                theme === "dark" ? "table-dark" : "table-light"
                              }`}>
                                <tbody>
                                <tr>
                                    <td>Subtotal:</td>
                                    <td className="text-end">${Number(invoiceData.financials?.subtotal || 0).toFixed(2)}</td>
                                  </tr>
                                  <tr>
                                    <td>Discount:</td>
                                    <td className="text-end">${Number(invoiceData.financials?.discount || 0).toFixed(2)}</td>
                                  </tr>
                                  <tr>
                                    <td>Tax:</td>
                                    <td className="text-end">${Number(invoiceData.financials?.tax || 0).toFixed(2)}</td> 
                                  </tr>
                                  <tr className="fw-bold">
                                    <td>Total:</td>
                                    <td className="text-end">${Number(invoiceData.financials?.total || 0).toFixed(2)}</td>
                                  </tr>
                               
                                </tbody>
                              </table>
                            </div>
                            <div className="col-md-6">
                              <h5>Customer Information</h5>
                              <div className="mb-2">
                                <strong>Name:</strong> {invoiceData.customer?.name || 'N/A'}
                              </div>
                              <div className="mb-2">
                                <strong>Email:</strong> {invoiceData.customer?.email || 'N/A'}
                              </div>
                              <div className="mb-2">
                                <strong>Phone:</strong> {invoiceData.customer?.phone || 'N/A'}
                              </div>
                              <div className="mb-2">
                                <strong>Address:</strong> {invoiceData.customer?.address || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Items Tab */}
                        <div className="tab-pane fade" id="items" role="tabpanel">
                          <div className="d-flex justify-content-between mb-3">
                            <h5>Invoice Items</h5>
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => setShowAddItemModal(true)}
                            >
                              <FaPlus /> Add Item
                            </button>
                          </div>
                          
                          <div className="table-responsive">
                            <table className={`table table-striped table-hover ${
                                theme === "dark" ? "table-dark" : "table-light"
                              }`} >
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Item</th>
                                  <th>Qty</th>
                                  <th>Price</th>
                                  <th>Discount</th>
                                  <th>Tax</th>
                                  <th>Total</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {products.length > 0 ? (
                                  products.map((product, index) => (
                                    <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{product.name}</td>
                                    <td>{product.quantity}</td>
                                    <td>${Number(product.price || 0).toFixed(2)}</td>
                                    <td>${Number(product.discountPrice || 0).toFixed(2)}</td>
                                    <td>{product.tax || 'N/A'}</td>
                                    <td>${Number(product.grand_total || 0).toFixed(2)}</td>
                                    <td>
                                      <button 
                                        className="btn btn-sm btn-outline-warning me-2"
                                        onClick={() => handleEditItem(product)}
                                      >
                                        <FaEdit />
                                      </button>
                                      <button 
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleRemoveItem(product.id)}
                                      >
                                        <FaTimes />
                                      </button>
                                    </td>
                                  </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="8" className="text-center py-4">
                                      No items found in this invoice
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Payments Tab */}
                        <div className="tab-pane fade" id="payments" role="tabpanel">
                          <div className="d-flex justify-content-between mb-3">
                            <h5>Payments</h5>
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => setShowAddPaymentModal(true)}
                            >
                              <FaPlus /> Add Payment
                            </button>
                          </div>
                          
                          <div className="table-responsive">
                            <table className={`table table-striped table-hover ${
                                theme === "dark" ? "table-dark" : "table-light"
                              }`}>
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th>Date</th>
                                  <th>Method</th>
                                  <th>Amount</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {payments.length > 0 ? (
                                  payments.map((payment, index) => (
                                    <tr key={index}>
                                      <td>{index + 1}</td>
                                      <td>{moment(payment.created_at).format('DD/MM/YYYY')}</td>
                                      <td>{payment.method}</td>
                                      <td>${Number(payment.amount || 0).toFixed(2)}</td>
                                      <td>
                                        <button 
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => handleRemovePayment(payment.id)}
                                        >
                                          <FaTimes />
                                        </button>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="5" className="text-center py-4">
                                      No payments recorded
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Options Tab */}
                        <div className="tab-pane fade" id="options" role="tabpanel">
                          <div className="d-flex flex-wrap gap-3">
                            <button 
                              className="btn btn-secondary"
                              onClick={handleDownloadPDF}
                            >
                              <FaDownload className="me-1" /> Download PDF
                            </button>
                            
                            <button 
                              className="btn btn-success"
                              onClick={() => setShowEmailModal(true)}
                            >
                             <FaEnvelope className="me-1" /> Email Invoice
                            </button>
                            
                            <button 
                              className="btn btn-primary"
                              onClick={() => setShowRefundModal(true)}
                            >
                              <FaUndo className="me-1" /> Process Refund
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Item Modal */}
      <div className={`modal fade ${showAddItemModal ? 'show d-block' : ''}`} id="addItemModal">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Item</h5>
              <button type="button" className="btn-close" onClick={() => setShowAddItemModal(false)}></button>
            </div>
            <form onSubmit={handleAddItem}>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-12">
                    <label className="form-label">Search Item</label>
                    <input
                      type="search"
                      className="form-control"
                      value={item.searchItem}
                      onChange={(e) => handleSearch(e.target.value)}
                      placeholder="Search by product name or barcode"
                    />
                    {searchResults.length > 0 && (
                      <div className="list-group mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {searchResults.map((product, index) => (
                          <button
                            type="button"
                            key={index}
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              setItem({
                                ...item,
                                product_id: product.id,
                                product_name: product.product_name,
                                price: product.sale_price,
                                searchItem: ''
                              });
                              setSearchResults([]);
                            }}
                          >
                            {product.product_name} ({product.barcode}) - ${product.sale_price}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Item Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="product_name"
                      value={item.product_name}
                      onChange={handleItemChange}
                      readOnly
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Quantity</label>
                    <input
                      type="number"
                      className="form-control"
                      name="quantity"
                      value={item.quantity}
                      onChange={handleItemChange}
                      min="1"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Price</label>
                    <input
                      type="number"
                      className="form-control"
                      name="price"
                      value={item.price}
                      onChange={handleItemChange}
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Discount</label>
                    <input
                      type="number"
                      className="form-control"
                      name="discount"
                      value={item.discount}
                      onChange={handleItemChange}
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Tax</label>
                    <select
                      className="form-select"
                      name="tax"
                      value={item.tax}
                      onChange={handleItemChange}
                    >
                      <option value="Exempt">Exempt</option>
                      {taxConfig.map(tax => (
                        <option key={tax.id} value={tax.name}>{tax.name} ({tax.rate}%)</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-12">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={item.description}
                      onChange={handleItemChange}
                      rows="2"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddItemModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                <FaPlus />  Add Item
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Item Modal */}
      <div className={`modal fade ${showEditItemModal ? 'show d-block' : ''}`} id="editItemModal">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Edit Item</h5>
              <button type="button" className="btn-close" onClick={() => setShowEditItemModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Item Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={item.product_name}
                    readOnly
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Quantity</label>
                  <input
                    type="number"
                    className="form-control"
                    name="quantity"
                    value={item.quantity}
                    onChange={handleItemChange}
                    min="1"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Price</label>
                  <input
                    type="number"
                    className="form-control"
                    name="price"
                    value={item.price}
                    onChange={handleItemChange}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Discount</label>
                  <input
                    type="number"
                    className="form-control"
                    name="discount"
                    value={item.discount}
                    onChange={handleItemChange}
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Tax</label>
                  <select
                    className="form-select"
                    name="tax"
                    value={item.tax}
                    onChange={handleItemChange}
                  >
                    <option value="Exempt">Exempt</option>
                    {taxConfig.map(tax => (
                      <option key={tax.id} value={tax.name}>{tax.name} ({tax.rate}%)</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={item.description}
                    onChange={handleItemChange}
                    rows="2"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowEditItemModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleUpdateItem}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Payment Modal */}
      <div className={`modal fade ${showAddPaymentModal ? 'show d-block' : ''}`} id="addPaymentModal">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Payment</h5>
              <button type="button" className="btn-close" onClick={() => setShowAddPaymentModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    name="amount"
                    value={paymentData.amount}
                    onChange={handlePaymentChange}
                    min="0.01"
                    step="0.01"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Method</label>
                  <select
                    className="form-select"
                    name="method"
                    value={paymentData.method}
                    onChange={handlePaymentChange}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={paymentData.date}
                    onChange={handlePaymentChange}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAddPaymentModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleAddPayment}>
              <FaPlus />  Add Payment
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email Invoice Modal */}
      <div className={`modal fade ${showEmailModal ? 'show d-block' : ''}`} id="emailInvoiceModal">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Email Invoice</h5>
              <button type="button" className="btn-close" onClick={() => setShowEmailModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">To</label>
                  <input
                    type="email"
                    className="form-control"
                    name="to"
                    value={emailInvoice.to}
                    onChange={handleEmailChange}
                    multiple
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">CC</label>
                  <input
                    type="email"
                    className="form-control"
                    name="cc"
                    value={emailInvoice.cc}
                    onChange={handleEmailChange}
                    multiple
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">BCC</label>
                  <input
                    type="email"
                    className="form-control"
                    name="bcc"
                    value={emailInvoice.bcc}
                    onChange={handleEmailChange}
                    multiple
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-control"
                    name="subject"
                    value={emailInvoice.subject}
                    onChange={handleEmailChange}
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Message</label>
                  <textarea
                    className="form-control"
                    name="messageBody"
                    value={emailInvoice.messageBody}
                    onChange={handleEmailChange}
                    rows="5"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowEmailModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleEmailInvoice}>
                Send Email
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Refund Modal */}
      <div className={`modal fade ${showRefundModal ? 'show d-block' : ''}`} id="refundModal">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Process Refund</h5>
              <button type="button" className="btn-close" onClick={() => setShowRefundModal(false)}></button>
            </div>
            <div className="modal-body">
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Price</th>
                      <th>Qty Sold</th>
                      <th>Discount</th>
                      <th>Qty to Refund</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product, index) => (
                      <tr key={index}>
                        <td>{product.name}</td>
                        <td>${Number(product.price || 0).toFixed(2)}</td>
                        
                        <td>{product.qty}</td>
                        <td>${Number(product.discountPrice || 0).toFixed(2)}</td>
                        <td>
                          <input
                            type="number"
                            className="form-control"
                            min="0"
                            max={product.qty}
                            value={refund.items[index]?.quantity || ''}
                            onChange={(e) => handleRefundItemChange(e, index, product.price,product.discountPrice)}
                          />
                        </td>
                        <td>${(refund.items[index]?.amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="row mt-3">
                <div className="col-md-12">
                  <label className="form-label">Reason for Refund</label>
                  <textarea
                    className="form-control"
                    name="reason"
                    value={refund.reason}
                    onChange={handleRefundChange}
                    rows="3"
                  />
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-6">
                  <label className="form-label">Refund Amount</label>
                  <input
                    type="text"
                    className="form-control"
                    value={`$${refund.refund_amount.toFixed(2)}`}
                    readOnly
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Payment Method</label>
                  <select
                    className="form-select"
                    name="refundPaymentMethod"
                    value={refund.refundPaymentMethod}
                    onChange={handleRefundChange}
                  >
                    <option value="deposit">Deposit</option>
                    <option value="cash">Cash</option>
                    <option value="credit">Credit</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowRefundModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleProcessRefund}>
                Process Refund
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop for modals */}
      {(showAddItemModal || showEditItemModal || showAddPaymentModal || showEmailModal || showRefundModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
    </Container>
            </div>
  );
};

export default UpdatePosInvoice;